import moment from 'moment-timezone';

//  Date formats
export const CALENDAR_DATE_FORMAT = 'DD/MM/YYYY';
export const SHORT_DATE_FORMAT = 'DD MMM';
export const SHORT_MONTH_AND_YEAR_FORMAT = 'MMM YYYY';

// Time formats
export const TWENTY_FOUR_HOUR_TIME_FORMAT = 'kk:mm';

const NON_BREAKING_SPACE = String.fromCharCode(160);

const todaysDateWithTime = ({ hours, minutes, meridiem }) => {
  if (hours === '' || minutes === '') return null;

  const time = moment(`${hours}:${minutes} ${meridiem}`, 'hh:mm A');
  if (!time.isValid()) return null;

  return time;
};

export const belowMinimumHours = (startDateTime, finishDateTime, minimumHours) => {
  const start = todaysDateWithTime(startDateTime);
  const finish = todaysDateWithTime(finishDateTime);

  if (start == null || finish == null) return false;

  // If the job is a ends after midnight then return false (i.e. finish time earlier than
  // start time). This has only ever happened once so finance are happy to consolidate these
  // manually for now.
  if (finish.isBefore(start)) {
    return false;
  }

  return moment.duration(finish.diff(start)).asHours() < minimumHours;
};

export const jobDateTimeWithZone = ({
  date, hours, minutes, meridiem,
}, timezone) => {
  const time = todaysDateWithTime({ hours, minutes, meridiem });
  if (!time || !date) return null;

  const datetime = {
    year: date.year(),
    month: date.month(),
    day: date.date(),
    hours: time.hours(),
    minutes: time.minutes(),
  };
  return moment.tz(datetime, timezone);
};

export const buildTime = (time, timezone) => {
  if (!time) {
    return {
      hours: '',
      minutes: '',
      meridiem: 'am',
      date: null,
    };
  }
  const timeWithZone = moment.tz(time, timezone);

  return {
    hours: timeWithZone.format('hh'),
    minutes: timeWithZone.format('mm'),
    meridiem: timeWithZone.format('a'),
    date: timeWithZone,
  };
};

// We want to stop using this method due to inflexibile regarding format and timezone
export const formatDateForView = dateTime => (
  dateTime ? moment(dateTime).format('MMM YYYY') : dateTime
);

export const formatDateTimeWithTimezone = (dateTime, format, timezone) => {
  if (!format) { console.error('No format provided for date time'); }
  if (!timezone) { console.error('No timezone provided for date time'); }

  return dateTime ? moment.tz(dateTime, timezone).format(format) : dateTime;
};

export const dateRangeToDuration = (startTime, finishTime) => {
  const momentStartTime = moment(startTime);
  const momentFinishTime = moment(finishTime);
  const duration = moment.duration(momentFinishTime.diff(momentStartTime));

  const roundedWeeks = Math.floor(duration.as('weeks'));
  const remainingMinutes = duration.minutes();
  const remainingHours = duration.hours();
  const remainingDays = duration.as('days') % 7;
  const roundedRemainingDays = duration.as('hours') > 24 && (remainingHours > 0 || remainingMinutes > 0) ? Math.ceil(remainingDays) : Math.floor(remainingDays);

  let minuteText = '';
  let hourText = '';
  let dayText = '';
  let weekText = '';

  if ((duration.as('hours') < 24) && remainingMinutes !== 0) {
    minuteText = remainingMinutes > 1 ? `${remainingMinutes} minutes` : `${remainingMinutes} minute`;
  }

  if ((duration.as('hours') < 24) && remainingHours !== 0) {
    hourText = remainingHours > 1 ? `${remainingHours} hours` : `${remainingHours} hour`;
  }

  if (roundedRemainingDays !== 0) {
    dayText = roundedRemainingDays > 1 ? `${roundedRemainingDays} days` : `${roundedRemainingDays} day`;
  }

  if (roundedWeeks !== 0) {
    weekText = roundedWeeks > 1 ? `${roundedWeeks} weeks` : `${roundedWeeks} week`;
  }

  return [
    weekText,
    dayText,
    hourText,
    minuteText,
  ].filter(value => value)
    .join(', ');
};

export const timeFromNow = date => moment(date).fromNow();
export const twentyFourHourTime = dateTime => moment(dateTime).format(TWENTY_FOUR_HOUR_TIME_FORMAT);

export const longDateWithTime = dateTime => (moment(dateTime).format('MMMM Do YYYY, h:mm a'));
export const shortDate = dateTime => (moment(dateTime).format(SHORT_DATE_FORMAT));
export const longDateWithWeekday = dateTime => (moment(dateTime).format(`ddd${NON_BREAKING_SPACE}D MMM${NON_BREAKING_SPACE}YYYY`));

export const longDate = dateTime => (
  dateTime ? moment(dateTime).format('DD MMMM YYYY') : dateTime
);

export const shortMonthAndDate = dateTime => (moment(dateTime).format(SHORT_MONTH_AND_YEAR_FORMAT));
